import { CloseIcon } from 'assets/icons/CloseIcon'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { CheckBox } from 'components/CheckBox/CheckBox'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import useModalClose from 'hooks/useModalClose'
import { useState } from 'react'
import { Box, Button, CursorPointer, Flex, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import {
  ASSIGNMENT_PICK,
  assignmentOptions,
  IAddTaskFormState,
  TASK_PRIORITIES,
  taskTemplateOptions,
} from 'types/operationsTasks.interface'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { WorkersSection } from './AddTaskModal.styled'

const parseDepartments = (data: any) =>
  data.data.map((item: any) => ({ label: item.name, value: item.id }))

const parseLocations = (data: any) =>
  data.data.map((i: any) => ({ label: i.name, value: i.id }))

const initialState: IAddTaskFormState = {
  taskTemplate: null,
  department: null,
  location: null,
  priority: 'Normal',
  assignment: assignmentOptions[0],
  startAt: new Date(),
  notes: '',
  evidenceRequired: false,
  workers: [],
}

export const AddTaskModal = ({ closeModal }: { closeModal: () => void }) => {
  const [state, setState] = useState<IAddTaskFormState>(initialState)
  const [selectedUser, setSelectedUser] = useState<ISelectValue | null>(null)

  useFocusFirstInput()
  useModalClose(closeModal)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSelect = (value: ISelectValue, name: string) => {
    setState((prev) => ({ ...prev, [name]: value }))
  }

  const handleAddWorker = () => {
    if (!selectedUser) return
    setState((prev) => ({
      ...prev,
      workers: prev.workers.some((w) => w.value === selectedUser.value)
        ? prev.workers
        : [...prev.workers, selectedUser],
    }))
    setSelectedUser(null)
  }

  const handleRemoveWorker = (userId: number) => {
    setState((prev) => ({
      ...prev,
      workers: prev.workers.filter((w) => w.value !== userId),
    }))
  }

  const handleCreate = () => {
    closeModal()
  }

  const showWorkersSection = state.assignment.value === ASSIGNMENT_PICK

  return (
    <Modal black>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>Add Task</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>

          <HonestSelectInput
            nomarg
            name='taskTemplate'
            labelText='Task template'
            options={taskTemplateOptions}
            placeholder='Select a template...'
            onChange={(v: ISelectValue) => setState((prev) => ({ ...prev, taskTemplate: v }))}
          />

          <HonestSelectAsyncInput
            nomarg
            link='departments'
            placeholder='Search department'
            name='department'
            labelText='Department'
            defaultValue={state.department?.label}
            onChange={(type: ISelectValue, name: string) => handleSelect(type, name)}
            parserFunction={parseDepartments}
          />

          <HonestSelectAsyncInput
            nomarg
            link='locations'
            placeholder='Search locations'
            name='location'
            labelText='Location (optional)'
            defaultValue={state.location?.label}
            onChange={handleSelect}
            parserFunction={parseLocations}
          />

          <Flex column gap='0.5rem' width='100%'>
            <TitleText >Priority</TitleText>
            <TabsContainer>
              {TASK_PRIORITIES.map((priority) => (
                <Tabs
                  key={priority}
                  active={state.priority === priority}
                  onClick={() => setState((prev) => ({ ...prev, priority }))}
                >
                  {priority}
                </Tabs>
              ))}
            </TabsContainer>
          </Flex>

          <Box>
            <HonestSelectInput
              nomarg
              name='assignment'
              labelText='Assignment'
              options={assignmentOptions}
              defaultValue={state.assignment.value}
              onChange={(v: ISelectValue) => setState((prev) => ({ ...prev, assignment: v }))}
            />
          </Box>

          {showWorkersSection && (
            <Box>
              <Flex gap='1rem' alignEnd>
                <HonestSelectAsyncInput
                  nomarg
                  link='users/basic'
                  name='employee'
                  labelText='Employee'
                  onChange={(v) => setSelectedUser({ ...v })}
                  parserFunction={(data: any) =>
                    data.data.map((i: any) => ({
                      label: i.fullName,
                      profilePictureUrl: i.getImagePresignedUrl,
                      value: i.id,
                      id: i.accessCardId,
                      role: i.role,
                      gender: i.gender,
                      tierPolicy: i.currentContract?.logisticsInfo?.accomodationTierPolicy,
                    }))
                  }
                />
                <Button orange onClick={handleAddWorker}>
                  Add
                </Button>
              </Flex>
              <Flex column gap='0.5rem'>
                {state.workers.length ? (
                  state.workers.map((worker) => (
                    <Flex key={worker.value} width='100%' between center>
                      <BodyText>{worker.label}</BodyText>
                      <CursorPointer onClick={() => handleRemoveWorker(worker.value)}>
                        <CloseIcon />
                      </CursorPointer>
                    </Flex>
                  ))
                ) : (
                  <OtherText style={{ marginTop: 5 }} grey>No employees added</OtherText>
                )}
              </Flex>
            </Box>
          )}

          <CalendarInput
            nomarg
            labelText='Start at'
            includeTime
            defaultValue={state.startAt}
            onChange={(date) => setState((prev) => ({ ...prev, startAt: date }))}
          />

          <Input
            nomarg
            name='notes'
            type='textarea'
            height='10rem'
            labelText='Notes (optional)'
            value={state.notes}
            onChange={handleChange}
          />

          <CheckBox
            square
            state={state.evidenceRequired}
            title='Evidence required on completion'
            onClick={() =>
              setState((prev) => ({ ...prev, evidenceRequired: !prev.evidenceRequired }))
            }
          />
        </Flex>
        <Button mt='16' orange onClick={handleCreate}>
          Create
        </Button>
      </Flex>
    </Modal >
  )
}
